import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  widthClassName?: string;
  maxWidthClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  showCloseButton?: boolean;
  closeLabel?: string;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  overlayClassName?: string;
  portalContentClassName?: string;
  centerClassName?: string;
  panelClassName?: string;
  fallbackTitle?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  widthClassName,
  maxWidthClassName = "max-w-md",
  contentClassName = "p-6",
  bodyClassName,
  showCloseButton = false,
  closeLabel = "Close",
  closeOnOverlayClick = true,
  closeOnEsc = true,
  overlayClassName = "bg-black/40 dark:bg-black/60",
  portalContentClassName = "z-50",
  centerClassName = "flex min-h-full items-start justify-center p-4 pt-20 sm:items-center sm:pt-4",
  panelClassName = "overflow-hidden rounded-lg border border-border-subtle bg-bg-surface shadow-xl animate-in zoom-in-95 fade-in duration-300",
  fallbackTitle = "Dialog",
}) => {
  const resolvedWidthClassName = widthClassName ?? maxWidthClassName;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={`fixed inset-0 z-50 animate-in fade-in duration-300 ${overlayClassName}`}
        />
        <Dialog.Content
          className={`fixed inset-0 overflow-y-auto ${portalContentClassName}`}
          {...(!description ? { "aria-describedby": undefined } : {})}
          onInteractOutside={(event) => {
            if (!closeOnOverlayClick) {
              event.preventDefault();
            }
          }}
          onEscapeKeyDown={(event) => {
            if (!closeOnEsc) {
              event.preventDefault();
            }
          }}
        >
          <div className={centerClassName}>
            <div
              className={`relative w-full ${resolvedWidthClassName} ${panelClassName} ${contentClassName}`}
            >
              {showCloseButton && (
                <Dialog.Close asChild>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={closeLabel}
                    className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-md text-text-muted hover:bg-bg-elevated hover:text-text-secondary"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              )}

              {(title || description) ? (
                <div className={`mb-4 min-w-0 ${showCloseButton ? "pr-10" : ""}`}>
                  <Dialog.Title className={title ? "text-lg font-medium text-text-primary" : "sr-only"}>
                    {title ?? fallbackTitle}
                  </Dialog.Title>
                  {description && (
                    <Dialog.Description className="mt-2 text-sm text-text-muted">
                      {description}
                    </Dialog.Description>
                  )}
                </div>
              ) : (
                <Dialog.Title className="sr-only">
                  {fallbackTitle}
                </Dialog.Title>
              )}

              <div className={bodyClassName}>{children}</div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
